import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { AdminTabs } from "../../components/AdminTabs.js";
import { PageSpinner } from "../../components/ui.js";

interface ProviderDescriptor {
  id: string;
  label: string;
  real: boolean;
  defaultModel: string;
}

interface AiInfo {
  providers: ProviderDescriptor[];
  taskRoutes: Record<string, string[]>;
  anyRealProvider: boolean;
}

interface Usage {
  total: number;
  byProvider: { provider: string; count: number; promptTokens: number; completionTokens: number }[];
  byFeature: { task: string; count: number; promptTokens: number; completionTokens: number; failures: number }[];
}

export function AdminAiPage() {
  const [info, setInfo] = useState<AiInfo | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setInfo(await api.get<AiInfo>("/api/admin/ai"));
        setUsage(await api.get<Usage>("/api/admin/ai/usage").catch(() => null));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Could not load AI configuration.");
      }
    })();
  }, []);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post<{ ok: boolean; provider: string; model: string; latencyMs: number; sample: string }>(
        "/api/admin/ai/test"
      );
      setTestResult({ ok: true, text: `✓ ${r.provider} (${r.model}) replied in ${r.latencyMs} ms: "${r.sample}"` });
    } catch (err) {
      setTestResult({ ok: false, text: err instanceof ApiError ? err.message : "Test failed." });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Administration</h1>
        <p className="subtitle">AI providers &amp; task routing.</p>
      </div>
      <AdminTabs />

      {error && <div className="alert alert-danger">{error}</div>}
      {!info ? (
        <PageSpinner />
      ) : (
        <div className="stack">
          {!info.anyRealProvider && (
            <div className="alert alert-info">
              No AI provider is configured yet. ClovaForm runs against the offline
              mock provider — every deterministic feature works, and AI-assisted
              features explain that a provider is needed. Add provider keys as
              ClovaForge environment variables to enable them.
            </div>
          )}

          {info.anyRealProvider && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Test connection</span>
                <button className="btn btn-sm btn-primary" onClick={runTest} disabled={testing}>
                  {testing ? "Testing…" : "Run test"}
                </button>
              </div>
              <p className="muted text-sm">Sends one tiny real request to the provider that handles coaching, to confirm the key and model actually work.</p>
              {testResult && (
                <div className={`alert mt-4 ${testResult.ok ? "alert-success" : "alert-danger"}`} style={{ marginBottom: 0 }}>
                  {testResult.text}
                </div>
              )}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <span className="card-title">Providers</span>
            </div>
            <p className="muted text-sm">
              Keys are server-side only and never sent to the browser. Blueprint
              is your private model server, reached over ClovaForge’s internal
              network.
            </p>
            <div className="table-card" style={{ border: "none", padding: 0 }}>
              {info.providers.map((p) => (
                <div className="tc-row" key={p.id}>
                  <span className="tc-label">
                    {p.label}{" "}
                    {p.real ? (
                      <span className="badge badge-success">configured</span>
                    ) : (
                      <span className="badge badge-muted">fallback</span>
                    )}
                  </span>
                  <span className="mono text-xs">{p.defaultModel}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Task routing</span>
            </div>
            <p className="muted text-sm">
              Each task prefers providers in order; the first configured one runs
              it, falling back down the list, with the offline mock as the floor.
            </p>
            <div className="table-card" style={{ border: "none", padding: 0 }}>
              {Object.entries(info.taskRoutes).map(([task, order]) => (
                <div className="tc-row" key={task} style={{ alignItems: "flex-start" }}>
                  <span className="tc-label mono text-xs">{task}</span>
                  <span className="text-xs" style={{ textAlign: "right" }}>
                    {order.join(" → ")}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {usage && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">AI usage</span>
                <span className="dim text-sm">{usage.total} call{usage.total === 1 ? "" : "s"} total</span>
              </div>
              <p className="muted text-sm">Cost protection: summaries and briefings are cached and only regenerated when the underlying data changes, so identical context isn't re-sent.</p>
              {usage.total === 0 ? (
                <p className="dim text-sm" style={{ marginBottom: 0 }}>No AI calls recorded yet.</p>
              ) : (
                <>
                  <div className="nav-section-label" style={{ padding: "var(--space-2) 0" }}>By feature</div>
                  <div className="table-card" style={{ border: "none", padding: 0 }}>
                    {usage.byFeature.map((f) => (
                      <div className="tc-row" key={f.task} style={{ alignItems: "flex-start" }}>
                        <span className="tc-label mono text-xs">{f.task}</span>
                        <span className="text-xs" style={{ textAlign: "right" }}>
                          {f.count} call{f.count === 1 ? "" : "s"}
                          {f.promptTokens + f.completionTokens > 0 ? ` · ${f.promptTokens + f.completionTokens} tok` : ""}
                          {f.failures > 0 ? ` · ${f.failures} failed` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="nav-section-label" style={{ padding: "var(--space-4) 0 var(--space-2)" }}>By provider</div>
                  <div className="table-card" style={{ border: "none", padding: 0 }}>
                    {usage.byProvider.map((p) => (
                      <div className="tc-row" key={p.provider}>
                        <span className="tc-label">{p.provider}</span>
                        <span className="text-xs">{p.count} · {p.promptTokens + p.completionTokens} tok</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
